import { Injectable } from '@angular/core';
import { Mimic } from 'icreate/common/models/mimic';
import _ from 'lodash';
import { CanvasService } from './canvas/canvas.service';
import { MatDialog } from '@angular/material/dialog';
import { PrintDialogComponent, PrintPageType, PrintType } from '@components/print-dialog/print-dialog.component';
import { lastValueFrom } from 'rxjs';
import { MimicFolderService } from './mimic-folder.service';
import { PullProjectService } from './pull-project.service';
import { LoadingDialogComponent } from '@layout/loading-dialog/loading-dialog.component';
import { RootItemType } from 'icreate/common/models/mimic-folder';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';

@Injectable({ providedIn: 'root' })
export class PrintMimicsService {
    constructor(
        private canvasService: CanvasService,
        private dialog: MatDialog,
        private mimicFolderService: MimicFolderService,
        private pullService: PullProjectService
    ) {}

    async triggerPrint(vessel, comp: boolean = false): Promise<boolean> {
        let loadingRef = null;
        let tempContainer: HTMLElement | undefined;
        let imagesContainer: { nativeElement: HTMLElement } | undefined;

        try {
            const rootList = await this.mimicFolderService.getByVesselId(vessel.id);
            const allMimics =
                rootList?.flatMap(item =>
                    item.type === RootItemType.mimic ? [item.mimic] : (item.folder?.mimicList ?? [])
                ) ?? [];

            const dialogRef = this.dialog.open(PrintDialogComponent, {
                data: {
                    pageType: PrintPageType.ALL_MIMICS,
                    vessel: vessel,
                    iAPIVesselId: vessel.iAPIVesselId,
                    shipyardId: vessel.shipyardId,
                    options: allMimics,
                    notMain: comp,
                },
            });

            const shouldPrint = await lastValueFrom(dialogRef.afterClosed());
            if (!shouldPrint) return true;
            if (shouldPrint.cancel) return false;

            const { printType, version, iAPIShipyardlId, setListIds, invertColor } = shouldPrint;

            if (!printType) {
                return false;
            }

            tempContainer = document.createElement('div');
            tempContainer.id = 'print-mimics-dynamic-container';
            document.body.appendChild(tempContainer);
            imagesContainer = { nativeElement: tempContainer };

            loadingRef = this.dialog.open(LoadingDialogComponent, {
                width: '320px',
                disableClose: true,
                data: { progress: 0 },
            });

            loadingRef.componentInstance.data.progress = 20;

            const selectedMimics = allMimics.filter(mimic => setListIds.has(mimic.id));
            const total = selectedMimics.length;

            if (printType === PrintType.NO_UPDATES) {
                for (let index = 0; index < total; index++) {
                    const mimic = selectedMimics[index];

                    const mimicToPrint = _.cloneDeep(mimic);

                    const svgContent = mimicToPrint.svgcontent;
                    const parseContent: any = new DOMParser().parseFromString(svgContent, 'text/xml');
                    const currentSvg = parseContent.documentElement;

                    const tempSvgContainer = document.createElement('div');
                    tempSvgContainer.style.position = 'absolute';
                    tempSvgContainer.style.visibility = 'hidden';
                    tempSvgContainer.style.pointerEvents = 'none';
                    tempSvgContainer.appendChild(currentSvg);
                    document.body.appendChild(tempSvgContainer);

                    const serializer = new XMLSerializer();
                    mimicToPrint.svgcontent = serializer.serializeToString(currentSvg);
                    document.body.removeChild(tempSvgContainer);

                    this.addMimicToPrint(mimicToPrint, imagesContainer, invertColor);
                    loadingRef.componentInstance.data.progress = Math.round(20 + ((index + 1) / total) * 60);
                }
            } else if (printType === PrintType.UPDATES) {
                if (!iAPIShipyardlId || !version) {
                    return false;
                }
                const oldMimics = await this.pullService.pullMimics(iAPIShipyardlId, vessel.iAPIVesselId, version);

                for (let index = 0; index < total; index++) {
                    const mimic = selectedMimics[index];
                    const mimicToPrint = _.cloneDeep(mimic);
                    const oldMimic = oldMimics.find(m => m.id === mimicToPrint.id);

                    const updatedSvg = this.compareMimic(mimicToPrint, oldMimic);
                    mimicToPrint.svgcontent = updatedSvg;

                    this.addMimicToPrint(mimicToPrint, imagesContainer, invertColor);
                    loadingRef.componentInstance.data.progress = Math.round(20 + ((index + 1) / total) * 60);
                }
            }

            loadingRef.componentInstance.data.progress = 90;

            await this.printMimicsAsPdf(imagesContainer, selectedMimics, invertColor, loadingRef);
            return true;
        } catch (error) {
            console.error('Error during mimic printing process:', error);
            throw error;
        } finally {
            if (loadingRef) {
                loadingRef.close();
            }
            if (tempContainer && tempContainer.parentNode) {
                tempContainer.parentNode.removeChild(tempContainer);
            }
        }
    }

    addMimicToPrint(view: Mimic, imagesContainer, invertColor: boolean = false) {
        const svgNS = 'http://www.w3.org/2000/svg';
        const groupSvg = document.createElementNS(svgNS, 'svg');

        let bkcolor = view?.profile?.bkcolor || '#131B23ff';

        if (invertColor && bkcolor.startsWith('#131B23')) {
            bkcolor = '#000';
        }

        let svgContents = view.svgcontent;

        if (!svgContents) return;

        if (!/<defs[\s>]/.test(svgContents)) {
            const defs = this.canvasService.getMimicDefs();
            svgContents = svgContents.replace(/(<svg[^>]*>)/, `$1${defs}`);
        }
        const newSVG = new DOMParser().parseFromString(svgContents, 'text/xml').getElementsByTagName('svg');

        if (!newSVG || newSVG.length === 0) return;

        const sourceSvg = newSVG[0];

        let viewBox = sourceSvg.getAttribute('viewBox');
        let width = sourceSvg.getAttribute('width');
        let height = sourceSvg.getAttribute('height');

        if (!viewBox && width && height) {
            viewBox = `0 0 ${parseFloat(width)} ${parseFloat(height)}`;
        } else if (!viewBox) {
            viewBox = '0 0 1240 908';
        }

        const parts = viewBox.split(/[\s,]+/).map(Number);
        const vbWidth = parts[2] || 1240;
        const vbHeight = parts[3] || 908;

        const rect = document.createElementNS(svgNS, 'rect');
        rect.setAttributeNS(null, 'x', '0');
        rect.setAttributeNS(null, 'y', '0');
        rect.setAttributeNS(null, 'width', vbWidth.toString());
        rect.setAttributeNS(null, 'height', vbHeight.toString());
        rect.setAttributeNS(null, 'fill', bkcolor);
        rect.setAttributeNS(null, 'stroke', '#000000ff');

        groupSvg.setAttributeNS(null, 'viewBox', `0 0 ${vbWidth} ${vbHeight}`);
        groupSvg.setAttributeNS(null, 'x', '0');
        groupSvg.setAttributeNS(null, 'y', '0');
        groupSvg.setAttributeNS(null, 'preserveAspectRatio', 'xMidYMid meet');
        groupSvg.setAttributeNS(null, 'overflow', 'hidden');
        groupSvg.setAttributeNS(null, 'width', `${vbWidth}px`);
        groupSvg.setAttributeNS(null, 'height', `${vbHeight}px`);

        sourceSvg.setAttributeNS(null, 'x', '0');
        sourceSvg.setAttributeNS(null, 'y', '0');
        sourceSvg.setAttributeNS(null, 'overflow', 'hidden');
        sourceSvg.setAttributeNS(null, 'width', `${vbWidth}px`);
        sourceSvg.setAttributeNS(null, 'height', `${vbHeight}px`);

        groupSvg.append(rect);
        groupSvg.append(sourceSvg);

        const b64Start = 'data:image/svg+xml;base64,';
        const xmlString = new XMLSerializer().serializeToString(groupSvg);
        const imageString = btoa(unescape(encodeURIComponent(xmlString)));
        const image64 = b64Start + imageString;
        const img = document.createElement('img');
        img.src = image64;
        img.setAttribute('overflow', 'hidden');

        const imagesDiv = imagesContainer.nativeElement;
        const div = document.createElement('div');
        div.setAttribute('class', 'imgContainer');
        div.setAttribute('overflow', 'hidden');
        div.append(img);
        imagesDiv.append(div);
    }

    compareMimic(currentMimic: Mimic, blobMimic: Mimic) {
        const svgContent = currentMimic.svgcontent;
        const parseContent: any = new DOMParser().parseFromString(svgContent, 'text/xml');
        const currentSvg = parseContent.documentElement;
        const tempContainer = document.createElement('div');
        tempContainer.style.position = 'absolute';
        tempContainer.style.visibility = 'hidden';
        tempContainer.style.pointerEvents = 'none';
        tempContainer.style.width = '0';
        tempContainer.style.height = '0';
        tempContainer.appendChild(currentSvg);
        document.body.appendChild(tempContainer);
        const layer = currentSvg.querySelector('.layer');

        if (!blobMimic) {
            const bbox = currentSvg.getBBox();
            this.addRectToLayer(bbox, layer);
            const serializer = new XMLSerializer();
            const updatedSvgContent = serializer.serializeToString(currentSvg);
            return updatedSvgContent;
        }
        const contentSavedView = new DOMParser().parseFromString(blobMimic.svgcontent, 'text/xml');
        const savedLayer = contentSavedView.querySelector('.layer');
        const changes = _.differenceWith(
            _.toPairs(this.extendedXml2json(layer)),
            _.toPairs(this.extendedXml2json(savedLayer)),
            _.isEqual
        );
        if (changes.length > 0) {
            changes.forEach(element => {
                if (element[0] && element[0] !== 'undefined' && element[0] !== '@attributes') {
                    const updatedElement: any = layer.querySelector('#' + element[0]);
                    if (updatedElement.tagName === 'g') {
                        this.onDetectChangesGroup(
                            currentMimic.id,
                            updatedElement,
                            parseContent,
                            contentSavedView,
                            layer,
                            undefined
                        );
                    } else {
                        const bbox = updatedElement.getBBox();
                        this.addRectToLayer(bbox, layer);
                    }
                }
            });
            const serializer = new XMLSerializer();
            const updatedSvgContent = serializer.serializeToString(currentSvg);
            return updatedSvgContent;
        }
        const serializer = new XMLSerializer();
        const updatedSvgContent = serializer.serializeToString(currentSvg);
        return updatedSvgContent;
    }

    addRectToLayer(bbox, layer) {
        const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        rect.setAttribute('x', (Number(bbox.x) - 10).toString());
        rect.setAttribute('y', (Number(bbox.y) - 10).toString());
        rect.setAttribute('height', (bbox.height + 15).toString());
        rect.setAttribute('width', (bbox.width + 15).toString());
        rect.setAttribute('fill', 'none');
        rect.setAttribute('stroke', 'red');
        layer.append(rect);
    }

    private onDetectChangesGroup(currentViewId, element, activeView, savedView, layer, changedItems) {
        if (changedItems === undefined) {
            changedItems = [];
        }
        const elementId = element.id;
        const savedGroup = savedView.querySelector('#' + elementId);
        const activeGroup = activeView.querySelector('#' + elementId);
        if (savedGroup) {
            const changes = _.differenceWith(
                _.toPairs(this.extendedXml2json(activeGroup)),
                _.toPairs(this.extendedXml2json(savedGroup)),
                _.isEqual
            );
            changes.forEach(element => {
                if (element[0] && element[0] !== 'undefined' && element[0] !== '@attributes') {
                    const newElement = layer.querySelector('#' + element[0]);
                    if (newElement && newElement.tagName === 'g') {
                        this.onDetectChangesGroup(
                            currentViewId,
                            newElement,
                            activeGroup,
                            savedGroup,
                            layer,
                            changedItems
                        );
                    } else if (newElement) {
                        const bbox = newElement.getBBox();
                        this.addRectToLayer(bbox, layer);
                    }
                }
            });
        }
        return changedItems;
    }

    private extendedXml2json(xml) {
        if (!xml) {
            return {};
        }
        let obj = {};
        if (xml.nodeType == 1 && xml.attributes.length > 0) {
            obj['@attributes'] = {};
            for (let j = 0; j < xml.attributes.length; j++) {
                const attribute = xml.attributes.item(j);
                if (attribute.nodeValue !== 'pointer-events:inherit') {
                    obj['@attributes'][attribute.nodeName] = attribute.nodeValue;
                }
            }
        } else if (xml.nodeType == 3) {
            obj = xml.nodeValue;
        }

        if (xml.hasChildNodes()) {
            for (let i = 0; i < xml.childNodes.length; i++) {
                const item = xml.childNodes.item(i);
                const nodeId = item.id;
                if (typeof obj[nodeId] == 'undefined') {
                    obj[nodeId] = this.extendedXml2json(item);
                } else {
                    if (typeof obj[nodeId].push == 'undefined') {
                        obj[nodeId] = [obj[nodeId]];
                    }
                    obj[nodeId].push(this.extendedXml2json(item));
                }
            }
        }

        return obj;
    }

    private convertSvgToPng(imgSrc: string, invertColor: boolean = false): Promise<string> {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.naturalWidth || 1240;
                canvas.height = img.naturalHeight || 933;
                const ctx = canvas.getContext('2d');
                if (ctx) {
                    if (invertColor) {
                        ctx.filter = 'invert(1) contrast(100%)';
                    }
                    ctx.drawImage(img, 0, 0);
                    resolve(canvas.toDataURL('image/png'));
                } else {
                    reject(new Error('Canvas context is null'));
                }
            };
            img.onerror = err => reject(err);
            img.src = imgSrc;
        });
    }

    async printMimicsAsPdf(imagesContainer, selectedMimics: Mimic[], invertColor: boolean = false, loadingRef?) {
        const images = imagesContainer.nativeElement.querySelectorAll('img');
        const content: any[] = [];
        const totalImages = images.length;

        for (let i = 0; i < totalImages; i++) {
            const img = images[i] as HTMLImageElement;
            const mimicName = selectedMimics[i]?.name || '';

            if (img.src) {
                const pngDataUrl = await this.convertSvgToPng(img.src, invertColor);

                content.push({
                    table: {
                        widths: ['*'],
                        heights: [550],
                        body: [
                            [
                                {
                                    alignment: 'center',
                                    valign: 'middle',
                                    margin: [15, 60, 0, 0],
                                    stack: [
                                        {
                                            table: {
                                                widths: [780],
                                                body: [
                                                    [
                                                        {
                                                            stack: [
                                                                {
                                                                    text: mimicName,
                                                                    fontSize: 16,
                                                                    bold: true,
                                                                    alignment: 'left',
                                                                    margin: [0, 0, 0, 8],
                                                                    font: 'MSGothic',
                                                                },
                                                                {
                                                                    image: pngDataUrl,
                                                                    width: 780,
                                                                    alignment: 'center',
                                                                },
                                                            ],
                                                            margin: [0, 0, 0, 0],
                                                        },
                                                    ],
                                                ],
                                            },
                                            layout: 'noBorders',
                                            alignment: 'center',
                                        },
                                    ],
                                },
                            ],
                        ],
                    },
                    layout: 'noBorders',
                    pageBreak: i < totalImages - 1 ? 'after' : undefined,
                });
            }
        }

        const pdfData = {
            pageOrientation: 'landscape',
            pageSize: 'A4',
            pageMargins: [15, 15, 15, 15],
            content: content,
            defaultStyle: {
                font: 'MSGothic',
            },
        };

        const runtimeVfs = {
            ...pdfFonts.default?.pdfMake?.vfs,
        };

        const baseHref = window.location.origin + '/';

        const fonts = {
            MSGothic: {
                normal: baseHref + 'assets/fonts/MSGothic.ttf',
                bold: baseHref + 'assets/fonts/MSGothic.ttf',
                italics: baseHref + 'assets/fonts/MSGothic.ttf',
                bolditalics: baseHref + 'assets/fonts/MSGothic.ttf',
            },
        };

        const pdfDocGenerator = pdfMake.default.createPdf(pdfData, null, fonts, runtimeVfs);

        const blob: Blob = await new Promise((resolve, reject) => {
            pdfDocGenerator.getBlob(
                (generatedBlob: Blob) => resolve(generatedBlob),
                (err: any) => reject(err)
            );
        });

        if (loadingRef) {
            loadingRef.componentInstance.data.progress = 100;
            loadingRef.close();
        }

        const blobUrl = URL.createObjectURL(blob);
        const newWindow = window.open(blobUrl, '_blank');

        if (!newWindow) {
            throw new Error('Popup blocker prevented opening the new window.');
        }

        newWindow.onload = () => {
            try {
                newWindow.print();
            } catch (error) {
                console.error('Error printing PDF:', error);
            }
        };

        setTimeout(() => {
            if (blobUrl) {
                URL.revokeObjectURL(blobUrl);
            }
        }, 300000);
    }
}
